import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { publicLinkError } from './errors';
import { formatDateTime } from './kit/time';
import { brandAssetUrl } from '../branding';
import {
  DollarSign, Loader2, CheckCircle2, XCircle, AlertCircle, ThumbsUp, ThumbsDown,
  Home, Building2, ChevronDown, ChevronRight, Sparkles, Clock, Download,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COMMENT_MAX = 2000; // matches the server's cap on the decision comment (ticket 198)
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const fmt$ = (n) => n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt$2 = (n) => n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

export default function BorrowerFeeApprovalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    publicApi.get(`/borrower/fee-worksheet/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(publicLinkError(e, 'Invalid or expired link')))
      .finally(() => setLoading(false));
  }, [token]);

  const decide = async (decision) => {
    setSubmitting(true);
    try {
      await publicApi.post(`/borrower/fee-worksheet/${token}/decide`, { decision, comment });
      toast.success(decision === 'approve' ? 'Approved. Thanks!' : 'Feedback sent to your loan officer.');
      // Reload
      const { data: d } = await publicApi.get(`/borrower/fee-worksheet/${token}`);
      setData(d);
    } catch (e) {
      toast.error(publicLinkError(e, 'Failed'));
      if (e.response?.status === 409) {
        // Already answered (ticket 198): show the recorded decision instead of the buttons.
        try {
          const { data: d } = await publicApi.get(`/borrower/fee-worksheet/${token}`);
          setData(d);
        } catch (_) { /* the toast already said it */ }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="animate-spin mr-2" size={16} /> Loading…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <AlertCircle size={40} className="mx-auto text-red-500" />
        <h1 className="mt-4 font-serif text-2xl font-bold text-slate-900">Link unavailable</h1>
        <p className="mt-2 text-sm text-slate-600">{error}</p>
      </div>
    </div>
  );

  const brandColors = data.brand?.colors || {};
  const NAVY = brandColors.primary || '#070B5F';
  const GREEN = brandColors.accent || '#6BA82E';
  const ws = data.worksheet;
  const finalized = data.approval.status === 'approved' || data.approval.status === 'rejected';
  const decidedOn = formatDateTime(data.approval.decided_at);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="borrower-fee-page">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex items-center gap-3 flex-wrap">
          {data.brand?.logo_url ? (
            <img src={brandAssetUrl(data.brand.logo_url)} alt={data.brand.company_name} className="h-9 w-auto" />
          ) : (
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: NAVY }}>
              <Building2 size={18} color="white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Closing cost estimate</div>
            <div className="font-serif text-lg font-bold" style={{ color: NAVY }}>{data.brand?.company_name || 'Your Lender'}</div>
          </div>
          <StatusChip status={data.approval.status} />
          <a
            href={`${BACKEND_URL}/api/borrower/fee-worksheet/${token}/pdf`}
            target="_blank" rel="noreferrer"
            data-testid="btn-borrower-pdf"
            className="ml-1 inline-flex items-center gap-1 text-xs px-2.5 h-8 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700"
          >
            <Download size={11} /> PDF
          </a>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-5">
        {/* Greeting card */}
        <div className="rounded-2xl p-6 text-white" style={{ background: NAVY }} data-testid="greeting-card">
          <div className="text-xs uppercase tracking-wider" style={{ color: GREEN }}>Prepared for you by</div>
          <div className="font-serif text-xl font-semibold mt-0.5">{data.lo_name}</div>
          <h1 className="mt-4 font-serif text-2xl font-bold">Hi {data.borrower_name},</h1>
          <p className="mt-1 text-sm opacity-80 max-w-2xl">
            Here's the initial closing cost estimate for your loan. Please review, then approve so we can proceed to
            ordering title work and locking your rate — or send back any questions.
          </p>
          {data.message_from_lo && (
            <blockquote className="mt-4 p-3 rounded-lg text-sm italic" style={{ background: 'rgba(255,255,255,0.08)' }}>
              "{data.message_from_lo}"
            </blockquote>
          )}
        </div>

        {/* Totals grid */}
        <div className="grid md:grid-cols-4 gap-3">
          <BigStat label="Purchase price" value={fmt$(ws.purchase_price)} navy={NAVY} testid="stat-price" />
          <BigStat label="Loan amount" value={fmt$(ws.loan_amount)} navy={NAVY} testid="stat-loan" />
          <BigStat label="Closing costs" value={fmt$(ws.totals.section_j_total_closing_costs)} navy={NAVY} testid="stat-closing" />
          <BigStat label="Est. cash to close" value={fmt$(ws.totals.estimated_cash_to_close)} navy={NAVY} accent={GREEN} testid="stat-ctc" />
        </div>

        {/* Sections */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="sections-container">
          <div className="p-5 border-b border-slate-100">
            <h2 className="font-serif text-lg font-bold" style={{ color: NAVY }}>Line-by-line breakdown</h2>
            <p className="text-xs text-slate-500 mt-1">These match the sections of your Loan Estimate (CFPB form).</p>
          </div>
          <div className="divide-y divide-slate-100">
            {ws.sections.map((s) => <BorrowerSection key={s.name} section={s} navy={NAVY} />)}
          </div>
        </div>

        {/* Decision */}
        {finalized ? (
          <div className="rounded-2xl p-6 border" style={{
            background: data.approval.status === 'approved' ? '#dcfce7' : '#fee2e2',
            borderColor: data.approval.status === 'approved' ? '#86efac' : '#fca5a5',
          }} data-testid="finalized-card">
            <div className="flex items-center gap-2">
              {data.approval.status === 'approved' ? <CheckCircle2 size={20} className="text-emerald-700" /> : <XCircle size={20} className="text-red-700" />}
              <div className="font-serif text-lg font-bold" style={{ color: data.approval.status === 'approved' ? '#166534' : '#991b1b' }}>
                {data.approval.status === 'approved' ? 'Thank you — approved!' : 'Feedback sent'}
              </div>
            </div>
            {decidedOn && (
              <p className="text-xs mt-1 text-slate-600" data-testid="decided-at">Answered {decidedOn}</p>
            )}
            {data.approval.borrower_comment && (
              <p className="text-sm mt-2 whitespace-pre-wrap break-words" style={{ color: data.approval.status === 'approved' ? '#166534' : '#991b1b' }} data-testid="decided-comment">
                Your note: "{data.approval.borrower_comment}"
              </p>
            )}
            <p className="text-xs mt-2 text-slate-600">Your loan officer has been notified.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="decision-card">
            <h2 className="font-serif text-lg font-bold" style={{ color: NAVY }}>Ready to move forward?</h2>
            <p className="text-sm text-slate-600 mt-1">Approve to lock in these numbers, or ask a question below.</p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={COMMENT_MAX}
              placeholder="Optional: leave a comment or question for your loan officer…"
              data-testid="borrower-comment"
              className="mt-3 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6BA82E]/40"
            />
            <div className="mt-4 flex gap-2 flex-wrap">
              <button onClick={() => decide('approve')} disabled={submitting}
                      data-testid="btn-approve"
                      className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 px-4 h-11 rounded-md text-white font-medium disabled:opacity-60"
                      style={{ background: GREEN }}>
                <ThumbsUp size={14} /> Approve
              </button>
              <button onClick={() => decide('reject')} disabled={submitting || !comment.trim()}
                      data-testid="btn-reject"
                      title={!comment.trim() ? 'Add a comment first' : ''}
                      className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 px-4 h-11 rounded-md text-white font-medium disabled:opacity-40"
                      style={{ background: '#dc2626' }}>
                <ThumbsDown size={14} /> Send back with questions
              </button>
            </div>
          </div>
        )}

        <p className="text-[11px] text-slate-400 text-center py-4">
          These figures are estimates from the LE calculator. Final numbers appear on your official Loan Estimate,
          delivered within 3 business days of your loan application.
        </p>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    sent: { bg: '#eff6ff', fg: '#1e40af', label: 'Awaiting your approval' },
    viewed: { bg: '#fef3c7', fg: '#92400e', label: 'Reviewing' },
    approved: { bg: '#dcfce7', fg: '#166534', label: 'Approved' },
    rejected: { bg: '#fee2e2', fg: '#991b1b', label: 'Sent back' },
  };
  const s = map[status] || map.sent;
  return <span className="text-[11px] font-bold uppercase px-3 py-1 rounded" style={{ background: s.bg, color: s.fg }} data-testid="status-chip">{s.label}</span>;
}
function BigStat({ label, value, navy, accent, testid }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-serif text-2xl font-bold mt-1" style={{ color: accent || navy }}>{value}</div>
    </div>
  );
}

function BorrowerSection({ section, navy }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-5 py-3.5 hover:bg-slate-50">
        {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        <span className="text-sm font-semibold flex-1 text-left" style={{ color: navy }}>{section.name}</span>
        <span className="text-sm font-mono font-semibold" style={{ color: navy }}>{fmt$2(section.subtotal)}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 bg-slate-50/50 divide-y divide-slate-100">
          {section.items.map((it, i) => (
            <div key={i} className="px-8 py-2 flex items-baseline gap-2">
              <span className="text-sm text-slate-700 flex-1">{it.name}</span>
              <span className="text-sm font-mono w-24 text-right" style={{ color: it.amount === 0 ? '#94a3b8' : navy }}>{fmt$2(it.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
